import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  MinusCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import { getBrandingDeployment } from '../services/api'
import type { AudienceTarget, BrandingDeployment } from '../services/api'
import { Badge, SectionCard, StatCard, type BadgeTone } from '../components/ui'
import DeviceInstallStatusTable from '../components/DeviceInstallStatusTable'
import { dateTime } from '../utils/date'

/**
 * ONE branding deployment.
 *
 * The list on Settings → Branding says what was SENT and to how many groups. It could not say what
 * HAPPENED, and those are different questions: a branding sits in Intune until each device checks
 * in, so "published to 2 groups" and "on 14 machines" can be hours apart and only the second one
 * answers "did it work".
 *
 * Deliberately the same shape as an application deployment's page — header, the counts, the
 * per-device table — because it is the same question about a different kind of thing, and the two
 * pages read the same Intune report through the same component. What is NOT here is an application
 * page's Automated updates and User experience: a branding has no version to follow and IS the user
 * experience, so those cards would have nothing to say.
 *
 * It goes one step further than the application page in one respect, and that is the point of it:
 * the assignment is resolved to DEVICES, so the table covers everything this branding reaches
 * rather than only what has reported. A machine assigned a branding that Intune has said nothing
 * about is the thing an operator comes here to find, and it appears in no report.
 */

const STATUS: Record<BrandingDeployment['status'], { label: string; tone: BadgeTone; hint: string }> = {
  publishing: {
    label: 'Publishing',
    tone: 'amber',
    hint: 'Being built, signed and put into Intune. It reaches no device until this finishes.',
  },
  published: {
    label: 'Published',
    tone: 'green',
    hint: 'In Intune and assigned. Devices pick it up on their next check-in.',
  },
  failed: {
    label: 'Failed',
    tone: 'red',
    hint: 'It did not reach Intune. The reason is below.',
  },
  withdrawing: {
    label: 'Removing',
    tone: 'blue',
    hint:
      'Assigned to uninstall. Devices restore the packaged branding on their next check-in; the ' +
      'Intune application is kept until then, because it is what removes it.',
  },
}

/** Intune's own intents, in its words. Only these two ever reach a branding: nobody browses the
 *  Company Portal for a re-skin, so `available` is never authored. */
const INTENT: Record<string, string> = {
  required: 'Required',
  uninstall: 'Uninstall',
  available: 'Available',
  availableWithoutEnrollment: 'Available without enrolment',
}

/** A target as a reader knows it: the group's name, or what a tenant-wide target reaches.
 *
 *  A group Entra would not name keeps its id. The assignment still points at that group, and
 *  showing nothing would hide that the branding is assigned to something that no longer exists. */
function targetName(t: AudienceTarget): string {
  if (t.type === 'allDevices') return 'All devices'
  if (t.type === 'allUsers') return 'All users'
  return t.name || t.id || 'Unnamed group'
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-theme-text-secondary">{label}</dt>
      <dd className="text-right font-medium text-theme-text-primary">{children}</dd>
    </div>
  )
}

export default function BrandingDeploymentDetail() {
  const { deploymentId } = useParams<{ deploymentId: string }>()

  const q = useIntuneQuery({
    queryKey: intuneKeys.brandingDeployment(deploymentId),
    queryFn: () => getBrandingDeployment(deploymentId!),
    enabled: !!deploymentId,
    retry: false,
  })
  const d = q.data

  if (q.isLoading) {
    return <p className="p-8 text-center text-theme-text-tertiary">Loading…</p>
  }
  if (q.isError && !d) {
    return (
      <div className="p-8 py-24 text-center">
        <p className="text-theme-text-tertiary">
          This branding rollout could not be read from Intune. {(q.error as Error).message}
        </p>
        <button
          type="button"
          onClick={() => q.refetch()}
          className="mt-2 text-sm text-theme-accent-blue hover:underline"
        >
          Try again
        </button>
      </div>
    )
  }
  if (!d) {
    return (
      <div className="p-8 py-24 text-center">
        <p className="text-theme-text-tertiary">Branding deployment not found.</p>
        <Link
          to="/settings/branding"
          className="mt-2 inline-block text-theme-accent-blue hover:underline"
        >
          Back to branding
        </Link>
      </div>
    )
  }

  const status = STATUS[d.status] ?? STATUS.publishing
  const targets = d.audience?.targets ?? []
  const inAudience = d.audience?.device_count ?? null
  const groupTargets = targets.filter((t) => t.type === 'group').length
  const audienceSummary = targets.length === 0
    ? 'Not assigned'
    : [
        groupTargets
          ? `${groupTargets} group${groupTargets === 1 ? '' : 's'}`
          : 'Tenant-wide',
        inAudience === null
          ? 'devices unresolved'
          : `${inAudience} device${inAudience === 1 ? '' : 's'}`,
      ].join(' · ')

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/settings/branding"
          className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-theme-text-tertiary hover:text-theme-text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to branding
        </Link>

        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-theme-text-primary">{d.name}</h1>
            <Badge tone={status.tone}>{status.label}</Badge>
          </div>
          <p className="mt-1 text-sm text-theme-text-secondary">
            {/* Named from the template it was authored from, which a deployment outlives: the
                identity is the content hash of what actually shipped, so a template edited since
                is a different branding and saying otherwise would name the wrong logo. */}
            {d.authored_from ? d.authored_from.name : 'Template edited or deleted since'}
            {' · '}
            <span className="font-mono text-[12px]">{d.identity?.slice(0, 12) ?? '—'}</span>
          </p>
          <p className="mt-1 text-[12px] text-theme-text-tertiary">{status.hint}</p>
        </div>

        {d.error && (
          <p className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-500">
            {d.error}
          </p>
        )}
        {d.intune_error && (
          <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-2.5 text-sm text-amber-500">
            {d.intune_error}
          </p>
        )}

        {/* The counts an application deployment leads with. Absent rather than zeroed when Intune
            could not be reached — zeros would say nobody has this branding when the truth is that
            nobody asked.

            NOT REPORTED IS THE FIFTH, and it only exists here: it counts devices this branding is
            assigned to that Intune has said nothing about, which the four Intune buckets cannot
            express. It is absent when the audience could not be resolved, because a count of
            machines nobody could enumerate would read as zero whatever the truth. */}
        {d.install_stats && (
          <div
            className={`mb-6 grid grid-cols-2 gap-4 ${
              d.install_stats.notReported != null ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
            }`}
          >
            <StatCard
              label="Installed"
              value={d.install_stats.installed}
              icon={CheckCircleIcon}
              tone="green"
            />
            <StatCard
              label="Pending"
              value={d.install_stats.pending}
              icon={ClockIcon}
              tone="amber"
            />
            <StatCard label="Failed" value={d.install_stats.failed} icon={XCircleIcon} tone="red" />
            <StatCard
              label="Not applicable"
              value={d.install_stats.notApplicable}
              icon={MinusCircleIcon}
              tone="slate"
            />
            {d.install_stats.notReported != null && (
              <StatCard
                label="Not reported"
                value={d.install_stats.notReported}
                icon={QuestionMarkCircleIcon}
                tone="blue"
                sub="assigned, nothing back yet"
              />
            )}
          </div>
        )}

        <SectionCard
          title="Branding contents"
          className="mb-6"
          collapsible
          defaultOpen={false}
          summary={d.settings?.length ? d.settings.join(', ') : 'Nothing taken over'}
        >
          <dl className="space-y-3 text-sm">
            <Row label="Company name">{d.company_name || '—'}</Row>
            <Row label="Dialog style">{d.dialog_style || '—'}</Row>
            <Row label="Takes over">
              {d.settings?.length ? d.settings.join(', ') : 'Nothing'}
            </Row>
            <Row label="Identity">
              <span className="font-mono text-[12px]">{d.identity || '—'}</span>
            </Row>
            <Row label="Published">
              {d.published_at ? dateTime(d.published_at) : 'Not yet'}
            </Row>
          </dl>
          <p className="mt-3 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
            A deployment keeps the appearance it shipped with. Editing the branding it came from
            never changes what a device already holds — that takes a new deployment.
          </p>
        </SectionCard>

        <SectionCard
          title="Assignment"
          className="mb-6"
          collapsible
          defaultOpen
          summary={audienceSummary}
          action={
            <Link
              to="/settings/branding"
              className="text-[12px] font-medium text-theme-accent-blue hover:underline"
            >
              Edit on Branding
            </Link>
          }
        >
          {/* GROUPS ARE STORED, DEVICES ARE NOT (models.py BrandingDeployment): membership changes
              constantly, so what each target holds is resolved when asked rather than captured
              here. The count on a row is that answer, and the table below names the machines. */}
          {targets.length === 0 ? (
            <p className="text-sm text-theme-text-tertiary">
              This branding reaches nobody. It stays in Intune until it is assigned or removed.
            </p>
          ) : (
            <ul className="space-y-2">
              {targets.map((t) => (
                <li
                  key={`${t.type}-${t.id ?? 'all'}-${t.intent}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-theme-text-primary">{targetName(t)}</span>
                      <Badge tone={t.intent === 'uninstall' ? 'amber' : 'blue'}>
                        {INTENT[t.intent] ?? t.intent}
                      </Badge>
                      {/* An exclusion subtracts from the audience; reading it as another group
                          that receives the branding is the opposite of the truth. */}
                      {!t.include && <Badge tone="slate">Excluded</Badge>}
                    </div>
                    {t.type === 'group' && (
                      <p className="mt-0.5 font-mono text-[11px] text-theme-text-tertiary">
                        {t.id}
                        {/* Only when Entra was actually asked. An unresolved audience has no name
                            for any group, and calling those deleted would invent a fault. */}
                        {t.name === null && !d.audience_error && ' — no longer in Entra'}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-[12px] text-theme-text-secondary">
                    {t.device_count == null
                      ? 'devices unresolved'
                      : `${t.device_count} device${t.device_count === 1 ? '' : 's'}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* Said here rather than left as blank counts: without the group read, the audience is
              unknown, not empty. */}
          {d.audience_error && (
            <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-500">
              {d.audience_error}
            </p>
          )}
          <p className="mt-3 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
            Membership is read now, not stored: a device added to one of these groups is in the
            audience straight away, with nothing republished.
          </p>
        </SectionCard>

        <h3 className="mb-1 text-sm font-semibold text-theme-text-primary">Devices</h3>
        <p className="mb-3 text-[12px] text-theme-text-tertiary">
          {d.audience_error
            ? 'Only the machines that have reported, because the assignment could not be resolved to devices.'
            : 'Every device this assignment reaches, and what each did with it. A device with ' +
              'nothing back yet reads as Not reported — it is in the audience and Intune has not ' +
              'said anything about it.'}
        </p>
        <div className="mb-6">
          <DeviceInstallStatusTable
            statuses={d.device_statuses}
            unavailableNote={
              d.intune_error ||
              'Intune could not be asked for per-device status. That is not the same as no devices.'
            }
          />
        </div>

        <p className="text-[12px] text-theme-text-tertiary">
          Status says whether the branding application INSTALLED, which is what puts the
          configuration on the machine. A device holds ONE branding whichever deployment installed
          last, so a machine listed here as installed while another branding was published to it
          later is showing that other one.
        </p>
      </div>
    </div>
  )
}
